/**
 * Person or entity marker: photo, initials, or a glyph placeholder for
 * pending invitations.
 * Spec: assets/handoffs/design_system_export/specs/Avatar.json
 *
 * Priority: src (photo) > initials > icon fallback. The spec allows
 * "AtSign or User01" for the fallback glyph; defaults to User01 here as the
 * more conventional person placeholder.
 *
 * @param {'xs'|'sm'|'md'|'lg'=md} size
 * @param {string=} src
 * @param {string=} initials - max 2 chars, uppercase; caller derives from display name
 * @param {ReactNode=} icon - overrides the default placeholder glyph
 * @param {'online'|'offline'|null=null} status
 * @param {string=} alt - required (as aria-label) when the avatar is standalone, not beside a visible name
 */
import React from "react";
import classNames from "classnames";
import Icon from "../icons/Icon";
import styles from "./Avatar.module.scss";

const Avatar = ({
  size = "md",
  src,
  initials,
  icon,
  status = null,
  alt,
  className,
  ...rest
}) => {
  const isStandalone = !!alt;

  return (
    <span
      className={classNames(styles.root, styles[`root--${size}`], className)}
      aria-hidden={isStandalone ? undefined : true}
      aria-label={isStandalone ? alt : undefined}
      {...rest}
    >
      <span className={styles.content}>
        {src ? (
          <img className={styles.image} src={src} alt="" />
        ) : initials ? (
          <span className={styles.initials}>{initials.slice(0, 2).toUpperCase()}</span>
        ) : (
          <span className={styles.placeholder}>
            {icon || <Icon name="User01" size={size === "xs" ? 14 : 20} />}
          </span>
        )}
      </span>
      {status && (
        <span className={classNames(styles.statusDot, styles[`statusDot--${status}`])} />
      )}
    </span>
  );
};

export const AvatarGroup = ({ avatars = [], max = 4, size = "md", className }) => {
  const visible = avatars.slice(0, max);
  const overflow = avatars.length - visible.length;

  return (
    <span className={classNames(styles.group, className)}>
      {visible.map((avatar, index) => (
        <span className={styles.groupItem} key={avatar.alt || avatar.initials || index}>
          <Avatar size={size} {...avatar} />
        </span>
      ))}
      {overflow > 0 && (
        <span className={styles.groupItem}>
          <span className={classNames(styles.root, styles[`root--${size}`])}>
            <span className={classNames(styles.content, styles.overflowChip)}>
              +{overflow}
            </span>
          </span>
        </span>
      )}
    </span>
  );
};

export default Avatar;
